import React, { useMemo } from 'react';
import { getTransformationParams } from '../../../../../utils/const/mapping';
import { useSelectedField } from '../../../../../utils/zustand';
import AddText from './AddText';
import AttributList from './AttributList';
import ConcatenateDelimiter from './ConcatenateDelimiter';
import GetAttribute from './GetAttribute';
import NumericOperations from './NumericOperations';
import SelectMedia from './SelectMedia';
import Translate from './Translate';

const TransformationForm = ({
  fieldTransformationType,
  fieldTransformationValue,
  onChange,
  fieldNamePrefix = [], // Préfixe pour les noms de champs Form.Item
}) => {
  const { selectedField } = useSelectedField();
  const paramsConfig = useMemo(
    () => getTransformationParams(fieldTransformationType),
    [fieldTransformationType]
  );

  const renderTransformationForm = () => {
    switch (fieldTransformationType) {
      case 'add_prefix':
      case 'add_suffix':
      case 'set_value':
        return (
          <AddText
            showSecondInput={false}
            label1={paramsConfig?.[0]?.label || 'Valeur'}
            placeholder1={`Entrez ${paramsConfig?.[0]?.label?.toLowerCase() || 'la valeur'}`}
            value={
              fieldTransformationValue?.value ||
              fieldTransformationValue?.prefix ||
              fieldTransformationValue?.suffix
            }
            onChange={(value) => {
              // Determine the key based on type
              let key = 'value';
              if (fieldTransformationType === 'add_prefix') key = 'prefix';
              if (fieldTransformationType === 'add_suffix') key = 'suffix';
              onChange({ ...fieldTransformationValue, [key]: value });
            }}
            fieldNamePrefix={fieldNamePrefix}
            paramKey={(() => {
              if (fieldTransformationType === 'add_prefix') return 'prefix';
              if (fieldTransformationType === 'add_suffix') return 'suffix';
              return 'value';
            })()}
          />
        );
      case 'add_prefix_suffix':
        return (
          <AddText
            showSecondInput={true}
            label1="Préfixe"
            placeholder1="Entrez le préfixe"
            value={fieldTransformationValue?.prefix}
            onChange={(value, key) => {
              // AddText returns value, key (value1 or value2)
              // We need to map value1->prefix, value2->suffix
              const paramKey = key === 'value1' ? 'prefix' : 'suffix';
              onChange({ ...fieldTransformationValue, [paramKey]: value });
            }}
            label2="Suffixe"
            placeholder2="Entrez le suffixe"
            value2={fieldTransformationValue?.suffix}
            fieldNamePrefix={fieldNamePrefix}
            paramKey="prefix"
            paramKey2="suffix"
          />
        );
      case 'multiply':
        return (
          <NumericOperations
            label={'Multiplier par'}
            placeholder={`Entrez le facteur de multiplication`}
            value={
              fieldTransformationValue?.factor ||
              fieldTransformationValue?.divisor
            }
            onChange={(val) => {
              const key =
                fieldTransformationType === 'multiply' ? 'factor' : 'divisor';
              onChange({ ...fieldTransformationValue, [key]: val });
            }}
            fieldNamePrefix={fieldNamePrefix}
            paramKey="factor"
          />
        );
      case 'divide':
        return (
          <NumericOperations
            label={paramsConfig?.[0]?.label || 'Multiplier ou Diviser'}
            placeholder={`Entrez ${paramsConfig?.[0]?.label?.toLowerCase() || 'la valeur'}`}
            value={
              fieldTransformationValue?.factor ||
              fieldTransformationValue?.divisor
            }
            onChange={(val) => {
              const key =
                fieldTransformationType === 'multiply' ? 'factor' : 'divisor';
              onChange({ ...fieldTransformationValue, [key]: val });
            }}
            fieldNamePrefix={fieldNamePrefix}
            paramKey="divisor"
          />
        );
      case 'set_first_attribute_value':
        return (
          // TODO: use same as transformation form for list of attributes
          <AttributList
            value={fieldTransformationValue?.fields}
            onChange={(val) =>
              onChange({ ...fieldTransformationValue, fields: val })
            }
            fieldNamePrefix={fieldNamePrefix}
          />
        );
      case 'get_attribute':
        return (
          <GetAttribute
            value={fieldTransformationValue?.attr}
            onChange={(val) =>
              onChange({ ...fieldTransformationValue, attr: val })
            }
            fieldNamePrefix={fieldNamePrefix}
          />
        );
      case 'set_media_value':
        return (
          <SelectMedia
            value={fieldTransformationValue}
            onChange={onChange}
            fieldNamePrefix={fieldNamePrefix}
          />
        );

      case 'concatenate':
        return (
          <ConcatenateDelimiter
            label="Délimiteur"
            value={fieldTransformationValue?.delimiter}
            onChange={(value) =>
              onChange({ ...fieldTransformationValue, delimiter: value })
            }
            fieldNamePrefix={fieldNamePrefix}
          />
        );
      case 'translate':
        return (
          <Translate
            value={fieldTransformationValue}
            onChange={onChange}
            defaultValues={{
              sourceLanguage: 'fr',
              destinationLanguage: selectedField?.lang,
            }}
            fieldNamePrefix={fieldNamePrefix}
          />
        );
      case 'truncate':
        return (
          <NumericOperations
            label={`Tronquer`}
            placeholder={`Entrez le nombre de caractère maximal de l'attribut`}
            value={fieldTransformationValue?.length}
            onChange={(val) => {
              onChange({ ...fieldTransformationValue, length: val });
            }}
            fieldNamePrefix={fieldNamePrefix}
            paramKey="length"
          />
        );
      default:
        return null;
    }
  };

  /**
   * Transformations qui n'ont pas besoin de formulaire supplémentaire
   * car elles n'ont que le champ source à configurer
   * Sauf pour list qui a une liste d'éléments à configurer, située dans la carte (et non dans le formulaire)
   */
  const TRANSFORMATIONS_WITHOUT_FORM = ['lowercase', 'uppercase', 'list'];

  const showTransformationForm = useMemo(() => {
    return (
      fieldTransformationType !== null &&
      !TRANSFORMATIONS_WITHOUT_FORM.includes(fieldTransformationType)
    );
  }, [fieldTransformationType]);

  if (!showTransformationForm) {
    return null;
  }

  return renderTransformationForm();
};

export default TransformationForm;
